/**
 * TextArea length helpers, aligned with the Opportunity Zod field ceilings.
 * Stored format is sanitized HTML; length checks use projected plain text.
 *
 * These live on the field side of the editor boundary: the character counter
 * and the over-limit message are the field's business rules, not something the
 * editor control knows about.
 */
import { convertToPlainText } from './convertToPlainText';
import {
  OPPORTUNITY_ADDITIONAL_INFORMATION_MAX,
  OPPORTUNITY_PROJECT_DESCRIPTION_MAX,
} from '../../validation/opportunityProjectFieldsSchema';

export function utf8ByteLength(value: string): number {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value).byteLength;
  }
  return encodeURIComponent(value).replace(/%[0-9A-F]{2}|./gi, 'x').length;
}

/** Matches `oppProjectDescription` Zod `.max(...)`. */
export const PROJECT_DESCRIPTION_MAX = OPPORTUNITY_PROJECT_DESCRIPTION_MAX;

/** Matches `siteAdditionalSiteInformation` Zod plain-text ceiling. */
export const ADDITIONAL_INFORMATION_MAX =
  OPPORTUNITY_ADDITIONAL_INFORMATION_MAX;

export type TextAreaLengthReport = {
  plainTextLength: number;
  htmlLength: number;
  maxLength: number;
  withinLimit: boolean;
  overBy: number;
  /** Zod-style message when over the field ceiling. */
  zodMessage: string | null;
};

export function stripHtmlToPlainText(html: string): string {
  return convertToPlainText(html);
}

export function reportTextAreaLength(
  html: string,
  maxLength: number,
  fieldLabel: string,
): TextAreaLengthReport {
  const plainTextLength = stripHtmlToPlainText(html).length;
  const htmlLength = html.length;
  const measured = plainTextLength;
  const withinLimit = measured <= maxLength;
  const overBy = withinLimit ? 0 : measured - maxLength;
  return {
    plainTextLength,
    htmlLength,
    maxLength,
    withinLimit,
    overBy,
    zodMessage: withinLimit
      ? null
      : `${fieldLabel} must be ${maxLength.toLocaleString()} characters or less (currently ${measured.toLocaleString()}, over by ${overBy.toLocaleString()}).`,
  };
}
